import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  MessageSquarePlus, 
  CheckCircle2, 
  X, 
  ShieldCheck, 
  Sparkles, 
  Quote, 
  Send, 
  Loader2,
  ThumbsUp,
  LayoutDashboard,
  Lock,
  Smartphone,
  Layers
} from 'lucide-react';
import { subscribeToReviews, submitPublicReview, ReviewItem } from '../src/services/reviewService';

export const Testimonials: React.FC = () => {
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'featured' | 'apps' | 'dashboards' | 'security'>('all');
  
  // Review form state
  const [author, setAuthor] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [quote, setQuote] = useState('');
  const [projectType, setProjectType] = useState('Web & Mobile Apps');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const unsubscribe = subscribeToReviews((data) => {
      setReviews(data);
    }, true);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!author.trim()) {
      setFormError('Please provide your name.');
      return;
    }
    if (!quote.trim() || quote.trim().length < 15) {
      setFormError('Please write at least a few words (15+ characters) about your experience.');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitPublicReview({
        author,
        role: role.trim() || 'Verified Client',
        company: company.trim(),
        email: email.trim(),
        quote: quote.trim(),
        rating,
        projectType
      });

      setIsSubmitting(false);
      setSubmittedSuccess(true);
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmittedSuccess(false);
        // Reset form
        setAuthor('');
        setRole('');
        setCompany('');
        setEmail('');
        setQuote('');
        setRating(5);
      }, 2200);
    } catch (err: any) {
      setIsSubmitting(false);
      setFormError(err?.message || 'Failed to submit review. Please try again.');
    }
  };

  const filteredReviews = reviews.filter((r) => {
    if (activeFilter === 'featured') return r.featured;
    if (activeFilter === 'apps') return r.projectType?.toLowerCase().includes('app');
    if (activeFilter === 'dashboards') return r.projectType?.toLowerCase().includes('dashboard') || r.projectType?.toLowerCase().includes('portal');
    if (activeFilter === 'security') return r.projectType?.toLowerCase().includes('security') || r.projectType?.toLowerCase().includes('rbac') || r.projectType?.toLowerCase().includes('login');
    return true;
  });

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length).toFixed(1)
    : '5.0';

  return (
    <section className="py-28 md:py-36 bg-[#070707] text-white border-t border-white/5 relative overflow-hidden" id="testimonials">
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[450px] bg-gradient-to-r from-[#d8aa68]/10 via-[#a77335]/5 to-transparent blur-[140px] pointer-events-none -z-10" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header replacing "Validation" */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d8aa68]/10 border border-[#d8aa68]/30 text-[#d8aa68] text-xs font-mono uppercase tracking-widest mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Public Reviews &amp; Client Feedback</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-white tracking-tight mb-5 leading-tight font-display">
            What Founders &amp; Teams Say, <br className="hidden sm:inline" />
            <span className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-[#d8aa68]">
              Open For Public Reviews
            </span>
          </h2>

          <p className="text-gray-400 text-sm sm:text-base md:text-lg font-light leading-relaxed mb-8">
            Transparent, unedited testimonials from founders, CTOs, and product leaders who engineered their web &amp; mobile apps, admin dashboards, login portals, and security infrastructure with me.
          </p>

          {/* Action Row: Rating stats + "Ask people to write a review" CTA */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 px-4 py-2.5 rounded-2xl backdrop-blur-sm">
              <div className="flex text-[#d8aa68]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="text-sm font-semibold text-white">{averageRating} / 5.0</span>
              <span className="text-xs text-gray-400 border-l border-white/10 pl-3">
                {reviews.length} Verified {reviews.length === 1 ? 'Review' : 'Reviews'}
              </span>
            </div>

            {/* WRITE A REVIEW BUTTON */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-[#d8aa68] to-[#b37a32] text-black font-semibold text-sm sm:text-base shadow-[0_4px_24px_rgba(216,170,104,0.25)] hover:shadow-[0_6px_30px_rgba(216,170,104,0.4)] transition-all cursor-pointer"
            >
              <MessageSquarePlus className="w-4 h-4" />
              <span>Write a Review</span>
            </motion.button>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center justify-center gap-2 flex-wrap mb-12">
          {[
            { id: 'all', label: 'All Reviews' },
            { id: 'featured', label: '⭐ Featured' },
            { id: 'apps', label: 'Apps & Mobile' },
            { id: 'dashboards', label: 'Admin Dashboards' },
            { id: 'security', label: 'Portals & Security' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id as any)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-mono tracking-wide transition-all ${
                activeFilter === tab.id
                  ? 'bg-[#d8aa68] text-black font-semibold shadow-sm'
                  : 'bg-white/[0.04] text-gray-400 hover:text-white hover:bg-white/[0.08] border border-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filteredReviews.map((item, idx) => (
            <motion.div
              key={item.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              viewport={{ once: true }}
              className="group relative p-6 sm:p-8 rounded-2xl bg-[#0c0c0c] border border-white/10 hover:border-[#d8aa68]/40 transition-all duration-300 shadow-lg flex flex-col justify-between"
            >
              {/* Top Meta: Stars & Project Tag */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex text-[#d8aa68]">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < item.rating ? 'fill-current' : 'text-gray-600'}`}
                      />
                    ))}
                  </div>

                  {item.projectType && (
                    <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-md bg-white/[0.05] text-[#d8aa68] border border-[#d8aa68]/20">
                      {item.projectType}
                    </span>
                  )}
                </div>

                {/* Review Text */}
                <div className="relative mb-6">
                  <Quote className="w-7 h-7 text-[#d8aa68]/20 absolute -top-3 -left-2 -z-0" />
                  <p className="relative z-10 text-gray-300 group-hover:text-white transition-colors duration-300 text-sm sm:text-base leading-relaxed italic">
                    "{item.quote}"
                  </p>
                </div>
              </div>

              {/* Author & Credibility Footer */}
              <div className="pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d8aa68]/30 to-[#4d2714]/40 border border-[#d8aa68]/40 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {item.author.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate font-display">
                      {item.author}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {item.role} {item.company ? `· ${item.company}` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full shrink-0">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Verified</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom invitation banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#d8aa68]/10 via-black to-[#4d2714]/15 border border-[#d8aa68]/20 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div>
            <h4 className="text-lg font-medium text-white mb-1">
              Have we collaborated or built a project together?
            </h4>
            <p className="text-sm text-gray-400">
              Share your honest feedback. Your review helps other founders evaluate our work on apps, dashboards, and security.
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white font-medium text-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <MessageSquarePlus className="w-4 h-4 text-[#d8aa68]" />
            <span>Leave a Review</span>
          </button>
        </div>

      </div>

      {/* WRITE A REVIEW MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111111] border border-[#d8aa68]/30 rounded-3xl max-w-lg w-full p-6 sm:p-8 relative shadow-[0_20px_60px_rgba(0,0,0,0.9)] max-h-[92vh] overflow-y-auto"
            >
              {/* Close button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-5 right-5 text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/5 transition-colors cursor-pointer"
                aria-label="Close review dialog"
              >
                <X className="w-5 h-5" />
              </button>

              {submittedSuccess ? (
                <div className="text-center py-10 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4 animate-bounce">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-display">
                    Thank You for Your Review!
                  </h3>
                  <p className="text-sm text-gray-300 max-w-sm mx-auto leading-relaxed">
                    Your feedback is published publicly to the reviews section and synced with the admin management console.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#d8aa68] font-bold">
                      Open to Public
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-white font-display">
                      Write a Review
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Share your experience working on apps, dashboards, login portals, or security.
                    </p>
                  </div>

                  {formError && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                      {formError}
                    </div>
                  )}

                  {/* Rating Selector */}
                  <div>
                    <label className="block text-xs font-mono uppercase text-gray-400 mb-1.5">
                      Your Rating *
                    </label>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setRating(star)}
                          className="p-1 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                        >
                          <Star
                            className={`w-6 h-6 ${
                              (hoverRating || rating) >= star
                                ? 'fill-[#d8aa68] text-[#d8aa68]'
                                : 'text-gray-600'
                            }`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-xs font-mono text-gray-400">
                        {rating} of 5 Stars
                      </span>
                    </div>
                  </div>

                  {/* Name & Role */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                        placeholder="e.g. Sarah Jenkins"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                        Role / Title
                      </label>
                      <input
                        type="text"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder="e.g. Founder / CTO / PM"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68]"
                      />
                    </div>
                  </div>

                  {/* Company & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                        Company or Project
                      </label>
                      <input
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="e.g. Nexus AI"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                        Email <span className="text-[10px] text-gray-500">(Private / Verification)</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68]"
                      />
                    </div>
                  </div>

                  {/* Project / Solution Type */}
                  <div>
                    <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                      What did we build or engineer together?
                    </label>
                    <select
                      value={projectType}
                      onChange={(e) => setProjectType(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#1a1a1a] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    >
                      <option value="Web & Mobile Apps">Custom Web &amp; Mobile Apps</option>
                      <option value="Admin Dashboards & Portals">Admin Dashboards &amp; Operations Portals</option>
                      <option value="Login Portals & Security">Secure Login Portals &amp; IAM</option>
                      <option value="Cybersecurity & RBAC">Cybersecurity, Auth Gateways &amp; RBAC</option>
                      <option value="Full-Stack System Architecture">Full-Stack System Architecture</option>
                      <option value="SaaS MVP & Growth Platform">SaaS MVP &amp; Growth Scaling</option>
                      <option value="General Collaboration">General Technical Advisory</option>
                    </select>
                  </div>

                  {/* Review Quote Text */}
                  <div>
                    <label className="block text-xs font-mono uppercase text-gray-400 mb-1">
                      Your Review / Experience *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={quote}
                      onChange={(e) => setQuote(e.target.value)}
                      placeholder="Share your thoughts on the speed, code quality, UI, architecture, dashboard, or security implementation..."
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68] resize-none"
                    />
                    <p className="text-[11px] text-gray-500 text-right mt-1">
                      {quote.length} characters (15 min)
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2.5 rounded-xl text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#d8aa68] to-[#b37a32] text-black font-semibold text-sm flex items-center gap-2 hover:opacity-95 transition-opacity disabled:opacity-50 cursor-pointer shadow-md"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Publishing...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Submit Public Review</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};